import { useState } from 'react'
import dayjs from 'dayjs'
import { ThumbsDown, ThumbsUp } from 'react-feather'
import { Button, Card, Modal, Text, TextField } from '@/components'
import { useAppDispatch } from '@/hooks'
import { surveyResponsesCollection } from '@/reducers/surveyResponses'
import { useUserSurveyOf, SurveyNames } from '@/app/hooks/useUserSurveyOf'

export type FeatureFeedbackRating = 'up' | 'down'

type ViewProps = {
  question: string
  onSubmit: (rating: FeatureFeedbackRating, feedback: string) => void
}

/**
 * Presentational half, exported for Storybook: the inline banner with
 * thumbs up / down, and the "Tell us more" modal a thumb opens.
 */
export function FeatureFeedbackBannerView({ question, onSubmit }: ViewProps) {
  const [rating, setRating] = useState<FeatureFeedbackRating | null>(null)
  const [feedback, setFeedback] = useState('')

  // No global preflight: strip the UA button chrome explicitly
  // grey-900, not grey-700: the palette has no grey-700 token, so the old
  // class was a silent no-op. 10.9:1 on purple-100 — well past the 3:1
  // WCAG non-text minimum for the icons.
  const thumbClass =
    'bg-transparent border-0 p-0 cursor-pointer text-themed-grey-900 flex items-center'

  return (
    <>
      {/* purple-100: the design system's subtle purple bg — the banner is a
          product ask, not a data card, so it reads distinct from the stats
          (!important: Card hardcodes bg-white) */}
      <Card fullWidth className="box-border !bg-themed-purple-100">
        <div className="flex items-center gap-4 h-auto">
          {/* grey-900 semibold: 10.9:1 on purple-100 (WCAG AAA) — body2's
              default grey-500 passed AA (5.8:1) but sat quiet on the tint */}
          <Text
            variant="body2"
            className="flex-1 !text-themed-grey-900 !font-semibold"
          >
            {question}
          </Text>
          <button
            aria-label="Thumbs up"
            className={thumbClass}
            onClick={() => setRating('up')}
          >
            <ThumbsUp size={20} />
          </button>
          <button
            aria-label="Thumbs down"
            className={thumbClass}
            onClick={() => setRating('down')}
          >
            <ThumbsDown size={20} />
          </button>
        </div>
      </Card>
      {rating !== null && (
        <Modal
          id="feature-feedback-modal"
          open
          onClose={() => setRating(null)}
          title="Thanks for the feedback!"
        >
          <div className="flex flex-col gap-4">
            <TextField
              id="feature-feedback-text"
              label="Tell us more"
              onChange={(event) => setFeedback(event.target.value)}
            />
            <Button
              id="feature-feedback-submit"
              variant="primary"
              onClick={() => {
                onSubmit(rating, feedback)
                // The banner stays mounted (retakeable survey) — close the
                // modal and clear the draft ourselves
                setRating(null)
                setFeedback('')
              }}
            >
              Submit
            </Button>
          </div>
        </Modal>
      )}
    </>
  )
}

type Props = {
  /** The Survey row's name — one FeatureFeedback survey per feature */
  surveyName: SurveyNames
  question: string
}

/**
 * Inline feature-feedback banner backed by the platform survey framework.
 * Permanent and retakeable: FeatureFeedback surveys stay pending after a
 * submission, and every submit is appended to the customer's single
 * SurveyResponse row for the survey (the `response` field becomes a list with
 * one entry per submission, each carrying its version and responded_at), so a
 * customer can send feedback again as the feature iterates. Reusable for any
 * feature rollout: create a Survey row with type "FeatureFeedback", add its
 * name to SurveyNames, and mount this with that name.
 */
export default function FeatureFeedbackBanner({ surveyName, question }: Props) {
  const dispatch = useAppDispatch()
  const response = useUserSurveyOf(surveyName)

  if (
    !response ||
    response.survey.type !== 'FeatureFeedback' ||
    !(response.pending_response || response.manual_show_flag)
  ) {
    return null
  }

  const onSubmit = (rating: FeatureFeedbackRating, feedback: string) => {
    // Append to this exact response row (not the by-type responder hook) so
    // several FeatureFeedback surveys can coexist on one page. The append
    // action keeps every submission on the one row and stamps
    // last_seen/completed server-side; responded_at dates each entry, same
    // as the MissingBrand appends.
    dispatch(
      surveyResponsesCollection.actions.update(
        `${response.id}/append`,
        {
          response: {
            rating,
            feedback: feedback || null,
            version: response.survey.version,
            responded_at: dayjs().toISOString(),
          },
        },
        { optimistic: true },
      ),
    )
  }

  return <FeatureFeedbackBannerView question={question} onSubmit={onSubmit} />
}
